import ArrowDownTrayIcon from "@heroicons/react/24/outline/ArrowDownTrayIcon";
import { cx } from "cva";
import { useSession } from "next-auth/react";
import { useMemo, useState } from "react";
import { MCPermissions } from "~/common/constants";
import { useAnalytics } from "~/components/Analytics";
import {
  canDownloadAsset,
  getUserPermissionsForAssets,
  requiresPlusPermissions,
} from "~/components/AssetDownload/assetDownloadHelpers";
import { BuildHtml } from "~/components/BuildHtml";
import { DataAccessChip } from "~/components/DataAccessChip";
import { usePiano } from "~/components/Piano/PianoContext";
import { commonConfig } from "~/config/common-config";
import { getPianoTemplateIds } from "~/lib/client/getPianoTemplateIds";
import { useFetchSignedAssetUrl } from "~/lib/client/useFetchSignedAssetUrl";
import { Body, Headline, IconButton, IconButtonProps } from "~/ui-library";
import { ChevronLeftIcon, ChevronRightIcon } from "~/ui-library/icons";
import { getButtonStyles } from "~/ui-library/shared-styles/shared-button-styles";

export type AssetDownloadEntryData = {
  attachmentId: string | number;
  attachmentTitle: string;
  fileDescription?: string | undefined;
  fileSize?: string;
  fileType: string;
  permission: (MCPermissions | "public")[];
};

type AssetDownloadEntryProps = AssetDownloadEntryData & {
  /**
   * Whether or not to hide the item in desktop breakpoints.
   * This is only true when pagination says it should hide.
   */
  isHidden: boolean;

  /**
   * Used for analytics. Does not need to be explicitly defined on each entry
   * and instead just passed as a prop from the parent component
   */
  postName: string;

  /**
   * Used for the endpoint route. Does not need to be explicitly defined on each entry
   * and instead just passed as a prop from the parent component
   */
  postId: string | number;
};

const permissionTitleMap = new Map<string, string>([
  [MCPermissions.PRO_CONSUMER_PLUS, "Industry+"],
  [MCPermissions.PRO_ECONOMIC_PLUS, "Economic+"],
  [MCPermissions.PRO_FULL_ACCESS_PERM, "Pro"],
  [MCPermissions.PRO_POLITICAL_PLUS, "Political+"],
  [MCPermissions.PRO_PERM, "Registered"],
  ["public", "Public"],
]);

export type AssetDownloadProps = {
  assets: AssetDownloadEntryData[];
  assetSummary?: string;
  postId: number;
  postName: string;
  showHeader?: boolean;
};

const FixedIconButton: React.FC<IconButtonProps> = ({
  SvgIcon,
  className,
  iconClassName,
  ...rest
}) => (
  <button
    className={cx(getButtonStyles({ ...rest, size: "none" }), className)}
    {...rest}
  >
    <SvgIcon className={iconClassName} height={20} width={20} />
  </button>
);

const AssetDownloadEntry = ({
  attachmentId,
  attachmentTitle,
  fileDescription,
  fileSize,
  fileType,
  isHidden,
  permission,
  postId,
  postName,
}: AssetDownloadEntryProps) => {
  const { analyticsEnhancedTrack } = useAnalytics();
  const { piano } = usePiano();
  const { data: session } = useSession();
  const userPermissions = useMemo(
    () => getUserPermissionsForAssets(session),
    [session]
  );
  const canDownload = canDownloadAsset(permission, userPermissions);
  const { isMutating, trigger } = useFetchSignedAssetUrl({
    postId,
    attachmentId,
    shouldFetch: canDownload,
  });

  const onDownload = async () => {
    analyticsEnhancedTrack("Data Asset Clicked", {
      assetName: attachmentTitle,
      assetPermission: permission.map((perm) => permissionTitleMap.get(perm)),
      hasAccess: canDownload,
      postName,
    });

    if (piano && !canDownload && permission[0] !== "public") {
      piano?.template.show({
        displayMode: "modal",
        showCloseButton: false,
        ...getPianoTemplateIds(commonConfig.NEXT_PUBLIC_ENDIL_ENV),
      });
      return;
    }

    const result = await trigger();

    if (result?.data.url) {
      const link = document.createElement("a");
      link.href = result?.data.url;
      link.download = "";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };
  return (
    <div
      className={cx(
        "flex flex-col border-b-1 border-dashed border-gray-300 py-s",
        isHidden ? "md:hidden" : ""
      )}
      data-testid={`${attachmentTitle} entry container`}
      key={attachmentId}
    >
      <div className="grid grid-cols-1 gap-xs md:grid-cols-[minmax(0,32rem),auto] md:gap-s">
        <div className="flex w-full flex-col gap-x-s gap-y-xxs">
          <div className="flex w-full">
            <Body
              className="self-center truncate text-gray-800"
              size="large"
              bold
              title={attachmentTitle}
            >
              {attachmentTitle}
            </Body>
            <div className="ml-s">
              {permission.includes("public") ||
              !requiresPlusPermissions(permission as MCPermissions[]) ? null : (
                <DataAccessChip
                  assetName={attachmentTitle}
                  postName={postName}
                  requiredPermissions={permission as MCPermissions[]}
                  userPermissions={getUserPermissionsForAssets(session)}
                />
              )}
            </div>
          </div>

          {fileDescription ? (
            <div className="flex text-body-xsmall text-gray-800">
              <BuildHtml html={fileDescription} />
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-xs md:grid md:grid-cols-[auto,minmax(0,75px),auto] md:gap-x-s md:justify-self-end">
          <Body
            bold
            className="uppercase text-gray-700 md:self-center md:justify-self-center"
            size="extraSmall"
          >
            {fileType}
          </Body>

          <Body bold className="text-gray-700 md:hidden" size="extraSmall">
            •
          </Body>
          <Body
            bold
            className="grow uppercase text-gray-700 md:grow-0 md:self-center md:justify-self-center"
            size="extraSmall"
          >
            {fileSize}
          </Body>

          <IconButton
            aria-label={`Download ${attachmentTitle}`}
            className="size-fit text-gray-700 xs:px-0 md:self-center md:justify-self-center"
            disabled={isMutating}
            iconClassName="block fill-transparent"
            onClick={onDownload}
            size="default"
            SvgIcon={ArrowDownTrayIcon}
            variant="subtle"
          />
        </div>
      </div>
    </div>
  );
};

function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunkedArray: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunkedArray.push(array.slice(i, i + chunkSize));
  }
  return chunkedArray;
}

type RangeTextProps = {
  pageStartIndex: number;
  pageEndIndex: number;
};

const RangeText: React.FC<RangeTextProps> = ({
  pageEndIndex,
  pageStartIndex,
}) => (
  <>
    {pageStartIndex}&ndash;{pageEndIndex}
  </>
);

const pageSize = 4;

export const AssetDownload: React.FC<AssetDownloadProps> = ({
  assets,
  assetSummary,
  postId,
  postName,
  showHeader = true,
}) => {
  const { analyticsEnhancedTrack } = useAnalytics();
  const downloadChunks = useMemo(() => chunkArray(assets, pageSize), [assets]);
  const [chunkOffset, setChunkOffset] = useState(0);

  const visibleDownloadList = downloadChunks[chunkOffset];

  if (!assets.length) {
    return null;
  }

  const chunkRangeStart = chunkOffset * pageSize + 1;
  const chunkRangeEnd = chunkRangeStart + visibleDownloadList.length - 1;

  const isEntryHidden = (entry: AssetDownloadEntryData) =>
    !visibleDownloadList.includes(entry);

  return (
    <div className="grid grid-cols-1" data-testid="downloadable-assets">
      <div className="flex items-end border-b-1 border-b-gray-300 pb-s">
        {showHeader && (
          <div className="flex flex-1 flex-col">
            <Headline as="h2" className="mb-xxs md:mb-xxs" level={2}>
              Data Downloads
            </Headline>

            <div className="max-w-[475px]">
              <Body className="text-gray-800" size="small">
                <BuildHtml allowStyles html={assetSummary} />
              </Body>
            </div>
          </div>
        )}

        {downloadChunks.length > 1 ? (
          <div className="hidden min-w-fit md:block">
            {/* controls */}
            <div className="order-0 flex items-center gap-xxs">
              <FixedIconButton
                aria-label="View previous page of downloads"
                className="p-xxs"
                disabled={chunkOffset === 0}
                iconClassName="block"
                onClick={() => {
                  setChunkOffset(chunkOffset - 1);
                  analyticsEnhancedTrack("Data Asset Pagination Clicked", {
                    direction: "previous",
                    postName,
                  });
                }}
                size="small"
                SvgIcon={ChevronLeftIcon}
                variant="subtle"
              />
              <div className="text-body-small">
                <RangeText
                  pageStartIndex={chunkRangeStart}
                  pageEndIndex={chunkRangeEnd}
                />
                <span className="text-gray-800">&nbsp;of&nbsp;</span>
                {assets.length}
              </div>
              <FixedIconButton
                aria-label="View next page of downloads"
                className="p-xxs"
                disabled={chunkOffset === downloadChunks.length - 1}
                iconClassName="block"
                onClick={() => {
                  setChunkOffset(chunkOffset + 1);
                  analyticsEnhancedTrack("Data Asset Pagination Clicked", {
                    direction: "next",
                    postName,
                  });
                }}
                size="small"
                SvgIcon={ChevronRightIcon}
                variant="subtle"
              />
            </div>
          </div>
        ) : null}
      </div>

      <div>
        {assets?.map((entry, index) => (
          <AssetDownloadEntry
            {...entry}
            key={`${entry.attachmentId}-${index}`}
            postName={postName}
            isHidden={isEntryHidden(entry)}
            postId={postId}
          />
        ))}
      </div>
    </div>
  );
};
